import * as React from 'react';

import { setLocation } from 'js/lib/coursera.redirect';
import { useParams } from 'js/lib/useRouter';

import { Typography2 } from '@coursera/cds-core';
import { StyleSheet, breakPoint, color, css } from '@coursera/coursera-ui';

import CourseAndPartnerName from 'bundles/course-cards/components/course-card/enterprise/CourseAndPartnerName';
import TogglableContent from 'bundles/course-cards/components/course-card/enterprise/TogglableContent';
import { useEnterpriseBadgeMetadata } from 'bundles/unified-home-common/contexts/EnterpriseBadgeMetadataContext';

import SkilledProfessionalDataProductCard from 'images/enterprise-learning-path-description-page/skilled-professional-data-product-card.png';

import _t from 'i18n!nls/enterprise-legacy-learner-home';

const styles = StyleSheet.create({
  BadgeCardHeader: {
    backgroundColor: color.white,
  },
  // TODO(jkemp): Aphrodite styles will be removed from program home by the end of Q2, didn't want to include the migration for this styling
  // in this PR
  CourseBadgePhoto: {
    width: 100,
    height: 100,
  },
  CourseBadgePhotoRounded: {
    borderRadius: 8,
  },
  hiddenMdDown: {
    [`@media (max-width: ${breakPoint.md}px)`]: {
      display: 'none',
    },
  },
});

const BadgeCardHeader = ({ isEnrolled, children }: { isEnrolled: boolean; children: React.ReactNode }) => {
  const badgeMetadata = useEnterpriseBadgeMetadata();
  const { programSlug } = useParams();

  if (!badgeMetadata) {
    return null;
  }

  const { badge, isSkillPathway } = badgeMetadata;
  const learningPathUrl = `/programs/${programSlug}/learning-path/${badge.slug}`;

  const handleImageClick = () => {
    setLocation(learningPathUrl);
  };

  const handleTitleClick = () => {
    setLocation(learningPathUrl);
  };

  const viewLessMoreContextLabel = _t('#{name}s description', { name: badge.title });

  return (
    <div {...css('rc-BadgeCardHeader', styles.BadgeCardHeader)}>
      <div className={`horizontal-box wrap ${isEnrolled && 'p-a-1'}`}>
        <div
          onClick={handleImageClick}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              handleImageClick();
            }
          }}
          role="button"
          tabIndex={0}
          style={{ cursor: 'pointer' }}
        >
          <img
            {...css(
              'm-r-1',
              styles.CourseBadgePhoto,
              isSkillPathway && styles.CourseBadgePhotoRounded,
              styles.hiddenMdDown
            )}
            src={isSkillPathway ? SkilledProfessionalDataProductCard : badge.imageUrl}
            alt={badge.title}
            width="100"
            height="100"
          />
        </div>
        <div className="c-description flex-1 pos-relative">
          <CourseAndPartnerName
            courseId={badge.badgeTemplateId}
            courseName={badge.title}
            partners={[]}
            isEnrolled={isEnrolled}
            onClick={handleTitleClick}
          />
          {badge.description && (
            <TogglableContent moreLessButtonContextLabel={viewLessMoreContextLabel}>
              <div>
                <Typography2 component="p" variant="bodySecondary" aria-label={badge.description}>
                  {badge.description}
                </Typography2>
              </div>
            </TogglableContent>
          )}
          {children}
        </div>
      </div>
    </div>
  );
};

export default BadgeCardHeader;
